import classNames from 'classnames'
import { useAnalytics } from 'src/common/analytics'
import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver'
import { useCallback, useEffect, useState } from 'react'
import { APP_BASE_PATH, LEGAL_BASE_PATH } from 'src/common/constants'
import Link from 'next/link'
import { ConsentButton } from './ConsentButton'
import { getCookies, setCookie } from 'src/common/helpers'

let hasTrackedView = false

const CONTENTFUL_ENTRY_ID = '2ITbD2j9Uz8uG2nKpPoggv'
const CONTENTFUL_ENTRY_VERSION = '2'
const CONSENT_BANNER_COOKIE_KEY = 'tracking_consent_banner'
const CONSENT_BANNER_LOCALSTORAGE_KEY = 'tracking_consent_banner_dismissed'
const CONSENT_BANNER_DISCLOSURE =
  'We use tracking technologies such as pixels and session replay tools to collect your information and interactions with our websites. By continuing to use our sites or closing this dialog, you acknowledge and agree to our use of tracking technologies. Please review our /privacy policy/ for more information.'

/** pending: reserved space, invisible | visible: shown | dismissed: removed from layout */
const CONSENT_STATUS = {
  PENDING: 'pending',
  VISIBLE: 'visible',
  DISMISSED: 'dismissed',
}

const isConsentDismissed = () => {
  try {
    if (localStorage.getItem(CONSENT_BANNER_LOCALSTORAGE_KEY)) {
      return true
    }
  } catch (e) {
    console.error('Failed to read consent from localStorage:', e)
  }

  return Boolean(getCookies()?.[CONSENT_BANNER_COOKIE_KEY])
}

export const ConsentBanner = () => {
  const [consentStatus, setConsentStatus] = useState(CONSENT_STATUS.PENDING)

  const { track } = useAnalytics()
  const { ref } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (hasTrackedView || !isIntersecting) return
      hasTrackedView = true
      if (isConsentDismissed()) return

      track(
        {
          additional_info: {
            disclosure_copy: CONSENT_BANNER_DISCLOSURE,
          },
          event_type: 'view',
          nav_link_section: 'TCB banner',
        },
        {},
        '_views/policy',
        '',
        'app'
      )
    },
    freezeOnceVisible: true,
  })

  const generateCookie = useCallback(() => {
    const parsedUrl = new URL(APP_BASE_PATH().replace('www.', ''))

    const visitId = getCookies()?.['eh_visit_id']
    const payload = JSON.stringify({
      visitId,
      contentfulId: CONTENTFUL_ENTRY_ID,
      contentfulVersion: CONTENTFUL_ENTRY_VERSION,
    })

    setCookie(CONSENT_BANNER_COOKIE_KEY, payload, {
      path: '/',
      domain: parsedUrl.hostname,
      expiration: null,
    })
  }, [])

  const dismissBanner = useCallback(() => {
    setConsentStatus(CONSENT_STATUS.DISMISSED)
  }, [])

  const handleAgree = useCallback(() => {
    const data = {
      list_name: 'Tracking consent click event',
      nav_link_section: 'TCB banner',
      click_text: 'Ok',
      event_action: 'button_click',
    }

    generateCookie()
    try {
      localStorage.setItem(
        CONSENT_BANNER_LOCALSTORAGE_KEY,
        JSON.stringify({
          dismissed: true,
          timestamp: new Date().toISOString(),
          action: 'agree',
        })
      )
    } catch (e) {
      console.error('Failed to save consent to localStorage:', e)
    }

    track(data, {}, 'button_click')
    dismissBanner()
  }, [generateCookie, track, dismissBanner])

  const handlePrivacyPolicyClick = useCallback(() => {
    const data = {
      click_id: 'privacy policy',
      click_text: 'FDR-Web | privacy policy',
      click_type: 'TCB banner Link Click',
      click_url: '/privacy/freedom-debt-relief-california-privacy-disclosures/',
      list_name: 'Policies link click event',
      nav_link_section: 'TCB banner',
      event_action: 'link_click',
    }

    track(data, {}, 'link_click')
  }, [track])

  const handleClose = useCallback(() => {
    const data = {
      list_name: 'Close consent click event',
      nav_link_section: 'TCB banner',
      event_action: 'button_click',
    }

    generateCookie()
    try {
      localStorage.setItem(
        CONSENT_BANNER_LOCALSTORAGE_KEY,
        JSON.stringify({
          dismissed: true,
          timestamp: new Date().toISOString(),
          action: 'close',
        })
      )
    } catch (e) {
      console.error('Failed to save consent to localStorage:', e)
    }

    track(data, {}, 'button_click')
    dismissBanner()
  }, [generateCookie, track, dismissBanner])

  useEffect(() => {
    const nextStatus = isConsentDismissed()
      ? CONSENT_STATUS.DISMISSED
      : CONSENT_STATUS.VISIBLE

    setConsentStatus((prev) => (prev === nextStatus ? prev : nextStatus))
  }, [])

  const isPending = consentStatus === CONSENT_STATUS.PENDING
  const isVisible = consentStatus === CONSENT_STATUS.VISIBLE
  const isDismissed = consentStatus === CONSENT_STATUS.DISMISSED

  return (
    <div
      ref={ref}
      aria-hidden={!isVisible}
      className={classNames(
        'w-content bg-white p-2 shadow-[0_27px_74px_0_rgba(29,37,47,0.10),0_13px_32px_0_rgba(29,37,47,0.08),0_3px_7px_0_rgba(20,46,87,0.05)] transition-opacity duration-200',
        isPending && 'pointer-events-none opacity-0',
        isVisible && 'opacity-100',
        isDismissed &&
          'pointer-events-none invisible h-0 overflow-hidden p-0 opacity-0 shadow-none'
      )}
    >
      <div className="flex flex-col items-start lg:flex-row lg:justify-between">
        <div className="flex" data-testid="tcb-banner-container">
          <p className="text-xs font-normal leading-[140%] text-content-primary">
            We use and allow cookies and similar tools on our websites to enable
            essential functionalities and to better understand how visitors use
            our site (including use of session replay technology), as well as
            for personalization, social media features, and advertising.
            <span className="hidden text-xs font-normal leading-[140%] text-content-primary lg:inline">
              {' '}
              Read our{' '}
              <Link
                className="text-blue-560"
                prefetch={false}
                href={`${LEGAL_BASE_PATH}/privacy/freedom-debt-relief-california-privacy-disclosures/`}
                onClick={handlePrivacyPolicyClick}
              >
                privacy policy
              </Link>{' '}
              to learn more. By closing this box or by your continued use of our
              sites, you accept such use.
            </span>
          </p>
          <div className="ml-2 lg:flex lg:items-center lg:gap-2">
            <div className="hidden lg:inline">
              <ConsentButton
                onClick={handleAgree}
                type={'info'}
                classnames={
                  'rounded-lg bg-[#EEF3FC] h-[30px] px-[24px] py-[2px] text-[18px] leading-[120%] tracking-[0.36px] font-bold text-blue-585'
                }
              />
            </div>
            <ConsentButton
              onClick={handleClose}
              classnames={'h-6 w-6 relative flex items-center justify-center'}
            />
          </div>
        </div>
        <div className="flex w-full items-end justify-between sm:items-start lg:hidden">
          <p className="text-xs font-normal leading-[140%] text-content-primary">
            Read our{' '}
            <Link
              className="text-blue-560"
              prefetch={false}
              href={`${LEGAL_BASE_PATH}/privacy/freedom-debt-relief-california-privacy-disclosures/`}
              onClick={handlePrivacyPolicyClick}
            >
              privacy policy
            </Link>{' '}
            to learn more. By closing this box or by your continued use of our
            sites, you accept such use.
          </p>
          <div className="ml-2">
            <ConsentButton
              onClick={handleAgree}
              type={'info'}
              classnames={
                'rounded-lg bg-[#EEF3FC] h-[30px] px-[24px] py-[2px] text-[18px] leading-[120%] tracking-[0.36px] font-bold text-blue-585'
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}
